// @flow

import React, { useState } from 'react';
import styled from '@emotion/styled';
import { get, head, isEmpty, map, sum, sumBy, without } from 'lodash/fp';
import { padding } from 'polished';
import download from 'downloadjs';
import numeral from 'numeral';

import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import Button from 'app/components/commons/Button';
import RadioButton from 'app/components/commons/RadioButton';
import Title from 'app/components/commons/Title';
import RadioTick from 'app/components/commons/RadioTick';
import Section from 'app/components/commons/Section';
import SelectedEntityRow from 'app/components/challenge/SelectedEntityRow';
import Clickable from 'app/components/commons/Clickable';
import SvgButton from 'app/components/commons/SvgButton';
import { downloadCrunchedLineUps, saveCrunchedLineUps } from 'app/api/challenges';
import t from 'app/utils/translate';

import LineUpGrid from '../LineUpGrid';
import ConfirmModal from '../ConfirmModal';
import CrunchSuccessModal from './CrunchSuccessModal';
import { Row } from './components';
import Spinner from '../../../components/commons/Spinner';

type Props = {
  isDesktop: boolean,
  lineUpsData: Array<*>,
  slateId: number,
  slateName: string,
  onDeleteLineUps: () => void
};

const Toolbar = styled.div(({ theme }) => ({
  backgroundColor: theme.color.panel,
  color: theme.color.panelAccent,
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing.xsmall,
  justifyContent: 'space-between',
  padding: theme.spacing.small
}));

const ButtonLabel = styled('span')(({ theme }) => ({
  whiteSpace: 'nowrap',
  ...padding(0, theme.spacing.tiny)
}));

const RemoveButton = styled(SvgButton)(({ theme }) => ({
  flexBasis: '30%',
  backgroundColor: theme.color.basis3,
  color: theme.color.white
}));

const ContestButtonContainer = styled.div(({ theme }) => ({
  display: 'flex',
  // justifyContent: 'flex-end',
  backgroundColor: theme.color.basis3
}));

const StretchButton = styled(Button)({
  display: 'flex',
  flexBasis: '70%',
  justifyContent: 'center',
  alignItems: 'center',
  outline: 'none'
});

const RacingCruncherLineUps = ({
  isDesktop,
  lineUpsData,
  onDeleteLineUps,
  slateId,
  slateName
}: Props) => {
  const [selectedLineupIds, setSelectedLineupIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [action, setAction] = useState(undefined);
  const [lineUps, setLineUps] = useState(lineUpsData);
  const [deletingLineUpId, setDeletingLineUpId] = useState(undefined);
  const [savedResults, setSavedResults] = useState(undefined);
  const [error, setError] = useState(undefined);

  const toggleSelectAll = () => {
    const ids = selectAll ? [] : map('id', lineUps);

    setSelectAll(!selectAll);
    setSelectedLineupIds(ids);
  };

  const handleToggleFavorite = (lineUpId: string, favorite: boolean) => {
    const items = [...lineUps];
    const index = lineUps.findIndex(l => l.id === lineUpId);
    items[index].favorite = favorite;
    setLineUps(items);

    if (favorite && !selectedLineupIds.includes(lineUpId)) {
      setSelectedLineupIds([...selectedLineupIds, lineUpId]);
    }
  };

  const handleDelete = (lineUpId: number) => {
    setLineUps(lineUps.filter(({ id }) => id !== lineUpId));
    setDeletingLineUpId(undefined);
  };

  const handleConfirmDelete = (lineUpId: number) => setDeletingLineUpId(lineUpId);

  const toggleSelectLineup = (id: number) => {
    if (selectedLineupIds.includes(id)) {
      setSelectAll(false);
      setSelectedLineupIds(without([id], selectedLineupIds));
      handleToggleFavorite(id, false);
    } else {
      setSelectedLineupIds([...selectedLineupIds, id]);
    }
  };

  const handleDownloadLineUps = () => {
    setAction('downloading');

    downloadCrunchedLineUps({
      lineups: lineUps
        .filter(({ id }) => selectedLineupIds.includes(id))
        .map(({ favorite, selection }) => ({
          selection: map('id', get('entities', head(selection))),
          featured: favorite
        })),
      slate_id: slateId
    })
      .then(blob => {
        download(blob, `${slateName}.csv`, 'text/csv');
      })
      .catch(({ errors }) => setError(get('message', errors)))
      .finally(() => setAction(undefined));
  };

  const handleSaveLineUps = () => {
    setAction('saving');

    saveCrunchedLineUps({
      lineups: lineUps
        .filter(({ id }) => selectedLineupIds.includes(id))
        .map(({ favorite, selection }) => ({
          selection: map('id', get('entities', head(selection))),
          featured: favorite
        })),
      slate_id: slateId
    })
      .then(({ data }) => setSavedResults(data))
      .catch(({ errors }) => setError(get('message', errors)))
      .finally(() => setAction(undefined));
  };

  const handleDeleteLineUps = () => {
    setLineUps([]);
    setSelectAll(false);
    onDeleteLineUps();
  };

  if (lineUps.length === 0) {
    return null;
  }

  return (
    <div>
      {isDesktop ? (
        <React.Fragment>
          <Title>Calculated Teams</Title>
          <h4>All calculated teams must be deleted to re-calculate new teams</h4>
        </React.Fragment>
      ) : (
        <Section
          title="Calculated Teams"
          titleHeight={40}
          titleStyle={{ justifyContent: 'center' }}
        >
          <Row justifyContent="center">Delete teams to re-calculate</Row>
        </Section>
      )}
      <Toolbar>
        <Clickable onClick={toggleSelectAll}>
          <RadioButton checked={selectAll} />
          <span>{'\u00a0\u00a0'}SELECT ALL</span>
        </Clickable>
        <h4>Selected Teams: {selectedLineupIds.length}</h4>
      </Toolbar>
      <Row justifyContent="center">
        {isDesktop && (
          <Button
            autoBorderRadius={false}
            kind="success"
            disabled={selectedLineupIds.length === 0 || action === 'downloading'}
            onClick={handleDownloadLineUps}
          >
            <div className="buttonInner">
              {action === 'downloading' && <Spinner size="small" inlineLeft />}
              <ButtonLabel>
                {t(`${action === 'downloading' ? 'Downloading...' : 'Download To CSV'}`)}
              </ButtonLabel>
            </div>
          </Button>
        )}
        <Button
          autoBorderRadius={false}
          kind="info"
          disabled={selectedLineupIds.length === 0 || action === 'saving'}
          onClick={handleSaveLineUps}
        >
          <div className="buttonInner">
            {action === 'saving' && <Spinner size="small" inlineLeft />}
            <ButtonLabel>
              {t(`${action === 'saving' ? 'Saving...' : 'Save To My Teams'}`)}
            </ButtonLabel>
          </div>
        </Button>
        <Button autoBorderRadius={false} kind="danger" onClick={handleDeleteLineUps}>
          <div className="buttonInner">
            <ButtonLabel>{t('Delete Teams')}</ButtonLabel>
          </div>
        </Button>
      </Row>
      {error && <FormValidationMessage message={error} />}
      <LineUpGrid
        lineUps={lineUps.map(l => ({
          ...l,
          projectedScore: numeral(
            sum(l.selection.map(({ entities }) => sumBy('scoreProjected', entities)))
          ).format('0,0.00')
        }))}
        isDesktop={isDesktop}
        renderBottomButtons={(selectionHash, lineupId) => (
          <ContestButtonContainer>
            <StretchButton
              autoBorderRadius={false}
              kind="secondary"
              onClick={() => toggleSelectLineup(lineupId)}
            >
              <ButtonLabel>Select</ButtonLabel>
              <RadioTick selected={selectedLineupIds.includes(lineupId)} />
            </StretchButton>
            <RemoveButton type="icon-bin" onClick={() => handleConfirmDelete(lineupId)} />
          </ContestButtonContainer>
        )}
        renderEntity={entity => (
          <SelectedEntityRow
            key={entity.idStr}
            awayTeam={entity.awayAbbr}
            bonus={entity.bonus}
            disabled={entity.disabled}
            gameOrder={entity.gameOrder}
            highlight={entity.highlight}
            showButtons={false}
            homeTeam={entity.homeAbbr}
            id={entity.id}
            idStr={entity.idStr}
            image={entity.imageURI}
            initials={entity.initials}
            isHome={entity.isHome}
            isRacing={entity.isRacing}
            isScratched={entity.isScratched}
            labels={entity.labels}
            locked={entity.locked}
            name={entity.name}
            number={entity.number}
            place={entity.placeProjected}
            positionId={entity.positionId}
            positionLabel={entity.positionAbbr || entity.positionId}
            reverseOrder={entity.reverseOrder}
            rider={entity.rider}
            riderImage={entity.riderImage}
            salary={entity.salary}
            trainer={entity.trainer}
            win={entity.winProjected}
          />
        )}
        showProjectedScore
        toggleLineupFavorite={handleToggleFavorite}
      />
      {!isEmpty(savedResults) && (
        <CrunchSuccessModal
          onCancel={() => setSavedResults(undefined)}
          successes={get('successes', savedResults)}
          failures={get('failures', savedResults)}
        />
      )}
      {deletingLineUpId && (
        <ConfirmModal
          onCancel={() => setDeletingLineUpId(undefined)}
          onSubmit={() => handleDelete(deletingLineUpId)}
          title="Delete this line up?"
          label="Delete"
        />
      )}
    </div>
  );
};

export default RacingCruncherLineUps;
